/** Board selection on the dashboard (P16-4): pure, keyed by board id, ranges follow the visual order. */
export type Selection = { ids: ReadonlySet<string>; anchor: string | null };

export const EMPTY_SELECTION: Selection = { ids: new Set(), anchor: null };

function range(order: readonly string[], from: string, to: string): string[] {
  const a = order.indexOf(from);
  const b = order.indexOf(to);
  if (a < 0 || b < 0) return [to];
  return order.slice(Math.min(a, b), Math.max(a, b) + 1);
}

/**
 * A click on `id`. Plain: only `id`, which becomes the anchor. `toggle` (Cmd/Ctrl): adds or removes `id`, the anchor
 * stays. `shift`: the range from the anchor to `id` replaces the selection, or is added to it with `toggle`.
 */
export function select(sel: Selection, id: string, order: readonly string[], mod: { shift: boolean; toggle: boolean }): Selection {
  if (mod.shift && sel.anchor) {
    const ids = new Set(mod.toggle ? sel.ids : []);
    for (const r of range(order, sel.anchor, id)) ids.add(r);
    return { ids, anchor: sel.anchor };
  }
  if (mod.toggle) {
    const ids = new Set(sel.ids);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    return { ids, anchor: sel.anchor ?? id };
  }
  return { ids: new Set([id]), anchor: id };
}

export function selectAll(order: readonly string[]): Selection {
  return order.length ? { ids: new Set(order), anchor: order[0]! } : EMPTY_SELECTION;
}

/** Keeps only what is still visible (a filter or a page change); the same object when nothing goes. */
export function prune(sel: Selection, visible: readonly string[]): Selection {
  const keep = new Set(visible);
  if ([...sel.ids].every((id) => keep.has(id)) && (!sel.anchor || keep.has(sel.anchor))) return sel;
  return { ids: new Set([...sel.ids].filter((id) => keep.has(id))), anchor: sel.anchor && keep.has(sel.anchor) ? sel.anchor : null };
}

const crosses = (r: DOMRectReadOnly, box: DOMRectReadOnly) =>
  r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top;

/** The cards a selection rectangle crosses; with `add` (Cmd/Ctrl or Shift held) they join `base`. */
export function selectRect(rects: ReadonlyMap<string, DOMRectReadOnly>, box: DOMRectReadOnly, base: Selection, add: boolean): Selection {
  const hits = [...rects].filter(([, r]) => crosses(r, box)).map(([id]) => id);
  const ids = new Set(add ? [...base.ids, ...hits] : hits);
  return { ids, anchor: base.anchor && ids.has(base.anchor) ? base.anchor : (hits[0] ?? null) };
}
